import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import utils from '@ehi/global-marketing-interface/lib/utils';
import { arrayToObjectByKey } from 'utils/array';
import { safeExecute } from 'utils/validation';
import { config } from 'utils/config';
import { getPin } from 'utils/locations';
import LOCATIONS from 'constants/locations';
import { getIntegerNumber } from 'utils/number';
import { propTypes } from './LocationMapPropTypes';
import LocationDetailTile from './LocationDetailTile';
import GoogleMapHOC from '../../Maps/GoogleMapHOC';
import LocationMapCTA from './LocationMapCTA';

const GOOGLE_MAPS_PADDING_TOP = 75;
const GOOGLE_MAPS_PADDING_BOTTOM = 200;

const LocationMap = ({
  breakpoint: breakpointSelector,
  selectedLocation: selectedLocationProp,
  map: mapProp,
  googleMaps: googleMapsProp,
  onDragEnd,
  onZoomChanged,
  isUserDraggedOrZoom,
  zoom,
  isVisible,
  center: centerProp,
  selectedLocationId,
  hoveredLocationId,
  locations,
  showLocationDetailTile,
  onMarkerEvents,
  timestamp: timeStampProp,
  onExpandLocation,
  handleSelectedLocation,
  setExpandedTab,
  setMobileExpanded,
  onSelectLocation,
  isReservationFlow,
  resetTimeStamp,
  onFitBounds,
  children,
  showCurrentLocation,
}) => {
  const { isMobile, isTabletOrMobile } = breakpointSelector;

  const [showRecenterButton, setShowRecenterButton] = useState(false);
  const [showSoldOutAreaBanner, setShowSoldOutAreaBanner] = useState(true);
  const [removedBanner, setRemovedBanner] = useState(false);
  const [googleMapsPaddingTop, setGoogleMapsPaddingTop] = useState(isMobile ? GOOGLE_MAPS_PADDING_TOP : 0);
  const [googleMapsPaddingBottom, setGoogleMapsPaddingBottom] = useState(
    isTabletOrMobile ? GOOGLE_MAPS_PADDING_BOTTOM : 0
  );

  const isVisibleRef = useRef(null);
  const mapRef = useRef(null);
  const googleMapsRef = useRef(null);
  const selectedLocationRef = useRef(selectedLocationProp || null);
  const locationMakers = useRef({});
  const activeMarker = useRef(null);
  const currentLocationMarker = useRef(null);
  const timestampRef = useRef(null);
  const currentBounds = useRef(null); // Store Markers Bounds
  const preventDragZoomChanges = useRef(false);

  // MapVisible function based on the prevstate of visible
  const isMapVisible = useMemo(() => {
    isVisibleRef.current = isVisible;
    return isVisible && !isVisibleRef.current;
  }, [isVisible]);

  /**
   * Set Small Icon Store Marker
   * @private
   */
  const setStandardIcon = (marker, label) => {
    marker?.setLabel?.({
      text: `${label}`,
      color: marker.isSoldOut ? '#414a5f' : '#fff',
      fontFamily: 'Poppins',
      fontWeight: 'bold',
      fontSize: '22px',
    });
    marker?.setIcon?.({
      url: getPin(marker.brand, marker.location_type, marker.isSoldOut, 'small'),
      scale: 1,
      labelOrigin: new googleMapsRef.current.Point(45 / 2, 24),
      scaledSize: new googleMapsRef.current.Size(45, 56),
      anchor: new googleMapsRef.current.Point(45 / 2, 56),
    });
    marker?.setZIndex?.(100 - getIntegerNumber(label));
    return marker;
  };

  /**
   * Set Large Icon Store Marker
   * @private
   */
  const setLargeIcon = (marker, label) => {
    marker?.setLabel?.({
      text: `${label}`,
      color: marker.isSoldOut ? '#414a5f' : '#fff',
      fontFamily: 'Poppins',
      fontWeight: 'bold',
      fontSize: '14px',
    });
    marker?.setIcon?.({
      url: getPin(marker.brand, marker.location_type, marker.isSoldOut, 'large'),
      scale: 1,
      labelOrigin: new googleMapsRef.current.Point(12, 12),
      scaledSize: new googleMapsRef.current.Size(73, 91),
      anchor: new googleMapsRef.current.Point(73 / 2, 91),
    });
    marker?.setZIndex?.(101);
    return marker;
  };

  /**
   * @private
   * Reset current Active Marker
   */
  const resetActiveMarker = () => {
    if (activeMarker.current) {
      setStandardIcon(activeMarker.current, activeMarker.current?.label?.text);
      activeMarker.current = null;
    }
  };

  /**
   * @private
   * Handles the highlight state of the Location Details tile through hovering over the markers on the map
   */
  const updateHoveredLocation = useCallback(
    (locationId) => {
      if (locationId) {
        const marker = locationMakers.current[locationId];
        resetActiveMarker();
        activeMarker.current = marker && setLargeIcon(marker, marker.label.text);
      } else {
        resetActiveMarker();
      }
    },
    [hoveredLocationId]
  );

  /**
   * @private
   * Handles the Expanding of Location Details tile through clicking the markers of on the map
   */
  const updateSelectedInternalLocation = useCallback(
    (locationId) => {
      if (locationId) {
        const marker = locationMakers.current[locationId];
        resetActiveMarker();
        activeMarker.current = marker && setLargeIcon(marker, marker.label.text);
      }
    },
    [selectedLocationId]
  );

  const handleMapDragStarted = () => {
    !removedBanner && setShowSoldOutAreaBanner(false);
  };

  const getMapBounds = () => {
    const center = mapRef.current.getCenter();
    const northEastCorner = mapRef.current.getBounds().getNorthEast();

    return {
      center,
      northEastCorner,
      radius: googleMapsRef.current.geometry.spherical.computeDistanceBetween(center, northEastCorner), // Radius in Meters
    };
  };

  /**
   * Debounces any panning of the mapRef
   */
  const handleMapDragged = utils.debounce((bounds) => {
    !removedBanner && setShowSoldOutAreaBanner(true);
    safeExecute(onDragEnd, bounds);
  }, 250);

  /**
   * Debounces any mapRef zoom interaction
   */
  const handleZoomChanged = utils.debounce((bounds) => {
    safeExecute(onZoomChanged, bounds);
  }, 250);

  /**
   * Bind Event Listeners
   * @private
   */
  const addMapListeners = () => {
    mapProp.addListener('click', () => {
      handleSelectedLocation(null);
      resetActiveMarker();
    });

    mapProp.addListener('dragstart', () => {
      if (!preventDragZoomChanges.current) {
        handleMapDragStarted();
      }
    });

    mapProp.addListener('dragend', () => {
      if (!preventDragZoomChanges.current) {
        const bounds = getMapBounds();
        handleMapDragged(bounds);
      }
    });

    mapProp.addListener('zoom_changed', () => {
      if (!preventDragZoomChanges.current) {
        const bounds = getMapBounds();
        handleZoomChanged(bounds);
      }
    });

    mapProp.addListener('bounds_changed', () => {
      // Ensure that fitBounds does not zoom too far in to 1 pin
      preventDragZoomChanges.current && !isUserDraggedOrZoom && mapProp.getZoom() > zoom && mapProp.setZoom(zoom);
    });
  };

  /**
   * Centers Map around all Markers
   */
  const handleRecenterMap = (e) => {
    //  If e is not defined it is getting called by changing breakpoints, we want to update the padding on GM to keep
    //  the pins in view.  But Comp will not update unless the timestamp is changed.  For the other uses of `handleRecenterMap`
    //  this is already accounted for
    if (e) {
      e.preventDefault();
    } else {
      resetTimeStamp();
    }

    setShowRecenterButton(false);
    safeExecute(onFitBounds, currentBounds.current);
  };

  useEffect(() => {
    setGoogleMapsPaddingTop(isMobile ? GOOGLE_MAPS_PADDING_TOP : 0);
    setGoogleMapsPaddingBottom(isTabletOrMobile ? GOOGLE_MAPS_PADDING_BOTTOM : 0);
  }, [isMobile, isTabletOrMobile]);

  /**
   * handles click marker events, will make the tiles scroll into view, and 'selects them'
   */

  const handleClickMarkerEvents = (locationId) => (e) => {
    const element = document.getElementById(locationId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    // Accessibility - to make selection on KeyboardEvent - To navigate map with a keyboard for screen reader users
    if (locationId) {
      const marker = locationMakers.current[locationId];
      resetActiveMarker();
      activeMarker.current = marker && setLargeIcon(marker, marker.label.text);
    } else {
      resetActiveMarker();
    }
    document.querySelector(`#location-${locationId} .map-location-detail-tile__link`)?.click();
    safeExecute(onMarkerEvents, e, locationId);
  };

  /**
   * Adds Current Location Marker
   * @param {Object} Google Map
   * @param {Object} Google Map LatLng Position
   */
  const addCurrentLocationMarker = (position) => {
    if (googleMapsRef.current?.Marker) {
      return null;
    }
    const map = mapRef.current;

    currentLocationMarker.current = new googleMapsRef.current.Marker({
      position,
      map,
      icon: {
        path: googleMapsRef.current.SymbolPath.CIRCLE,
        scale: 9,
        fillColor: '#8c4c9e',
        fillOpacity: 1,
        strokeColor: '#cd94ff',
        strokeOpacity: 0.73,
        strokeWeight: 8,
      },
      zIndex: 0,
    });
    return currentLocationMarker.current;
  };

  /**
   * Center Map Around all Store Markers
   */
  const fitBounds = (locationsParam) => {
    preventDragZoomChanges.current = true;
    if (selectedLocationProp?.gps) {
      const {
        gps: { latitude, longitude },
      } = selectedLocationProp;
      const position = { lat: Number(latitude), lng: Number(longitude) };

      if (selectedLocationProp.type === LOCATIONS.TYPE_GEOLOCATION && showCurrentLocation) {
        addCurrentLocationMarker(position);
      }
      if (utils.isArrayNotEmpty(locationsParam)) {
        currentBounds.current &&
          mapRef.current.fitBounds(currentBounds.current, {
            bottom: googleMapsPaddingBottom,
            top: googleMapsPaddingTop,
          });
      } else {
        mapRef.current.setCenter(position);
      }
    } else {
      mapRef.current.setCenter(centerProp);
    }

    googleMapsRef.current.event.addListenerOnce(mapRef.current, 'idle', () => {
      setShowRecenterButton(false);
      preventDragZoomChanges.current = false;
    });
  };

  /**
   * @private
   * Updates map to new selected location
   * Show Recenter button if is user dragged or zoomed
   * Hide Recenter button if new location is not user interacted or if new location is same as previous location
   */
  const updateSelectedLocation = (selectedLocationItem, locationsParam) => {
    const isDifferentLocation =
      (selectedLocationItem && selectedLocationRef.current?.id !== selectedLocationItem.id) ||
      (!selectedLocationRef.current && selectedLocationItem) ||
      (selectedLocationRef.current && !selectedLocationItem);

    if (isDifferentLocation) {
      selectedLocationRef.current = selectedLocationItem;
      fitBounds(locationsParam, { bottom: googleMapsPaddingBottom, top: googleMapsPaddingTop });
    }
  };
  /**
   * Handle Marker Interactions
   */
  const handleMarkerEvents = (locationId) => (e) => {
    safeExecute(onMarkerEvents, e, locationId);
  };

  /**
   * Add a Store Marker
   * @private
   * @returns {Object} Google Map Marker
   */
  const addLocationMarker = (location, label) => {
    const {
      brand,
      id,
      name,
      gps: { latitude, longitude },
      additional_data,
    } = location;
    const map = mapRef.current;

    const marker =
      googleMapsRef.current?.Marker &&
      Object.assign(new googleMapsRef.current.Marker({ map, position: { lat: latitude, lng: longitude } }), {
        brand,
        id: location.id,
        location_type: location.location_type,
        name,
        address: location.address,
        additional_data,
        locationData: location,
        isSoldOut:
          config.getIsSoldOutTrueAvailability() && location.location_availability === LOCATIONS.UNAVAILABLE_LOCATIONS,
      });

    marker?.setTitle(name);
    setStandardIcon(marker, label);

    marker?.addListener('click', handleClickMarkerEvents(id));
    marker?.addListener('mouseover', handleMarkerEvents(id));
    marker?.addListener('mouseout', handleMarkerEvents(id));

    return marker;
  };

  /**
   * Add a List of Store Markers
   * @param {Array} Marker data object
   */
  const addLocationMarkers = (locationsParam) => {
    const markers = locationsParam.map((location, index) => addLocationMarker(location, index + 1));

    currentBounds.current = new googleMapsRef.current.LatLngBounds();
    locationMakers.current = arrayToObjectByKey(markers, 'id');
    markers.forEach((marker) => currentBounds.current.extend(marker.position));
  };

  const handleExpandLocation = () => {
    onExpandLocation(selectedLocationId);
  };

  const handleDeselectOfBranch = () => {
    handleSelectedLocation(null);
  };

  const handleAdjustDateTime = () => {
    handleDeselectOfBranch();
    setExpandedTab('datesTimes');
    isMobile && setMobileExpanded(isMobile);
  };

  const hideSoldOutAreaBanner = () => {
    setRemovedBanner(true);
    setShowSoldOutAreaBanner(false);
  };

  /**
   * Remove All Location Markers from Map
   */
  const clearLocationMarkers = () => {
    Object.keys(locationMakers.current).forEach((key) => locationMakers.current?.[key].setMap(null));
  };

  /**
   * @private
   * Handles the visibility of the recenter button when new locations results are updated
   */
  const updateRecenterButtonVisibility = (selectedLocationItem, isUserDraggedOrZoomValue, hasLocations) => {
    // Don't show Recenter button if the searched location was entered via the input field (vs. panning the map to another location)
    // Or if partner locations have been rendered after a user has dragged & zoomed
    const partnerLocationsRendered = isUserDraggedOrZoomValue && hasLocations;
    if (showRecenterButton && (!isUserDraggedOrZoomValue || partnerLocationsRendered)) {
      setShowRecenterButton(false);
    } else if (selectedLocationItem && !showRecenterButton && isUserDraggedOrZoomValue) {
      // Show Recenter button if user panned or zoomed on the map to a new location
      setShowRecenterButton(true);
    }
  };

  /**
   * @private
   * Updates map with new markers based on new locations results. Update is triggered with a new timestamp as it
   * is more effiecient than doing a deep comparison of locations array
   */
  const updateMarkers = useCallback(
    (locationsParam, selectedLocationItem, timeStampValue, isUserDraggedOrZoomValue) => {
      const hasLocations = utils.isArrayNotEmpty(locationsParam);

      !hasLocations && (currentBounds.current = null);
      // If new timestamp is set, it's an indication that there are new locations results or no locations found
      if (timestampRef.current !== timeStampValue) {
        timestampRef.current = timeStampValue;
        clearLocationMarkers(); // Clear any existing location markers to update map with new results

        if (hasLocations) {
          addLocationMarkers(locationsParam); // If there are new results, generate new markers on the map
          // If the current location was searched via Input, then center the map around this new location
          // Do nothing if the location was searched through panning the map
          !isUserDraggedOrZoomValue &&
            fitBounds(locationsParam, { bottom: googleMapsPaddingBottom, top: googleMapsPaddingTop });
          const marker = locationMakers.current[selectedLocationId];
          if (marker) {
            activeMarker.current = setLargeIcon(marker, marker.label.text);
          } else {
            //  If the selected location is not in the new batch of locations, we should clear the selected location
            handleSelectedLocation(null);
          }
        } else {
          //  If we have no locations we should clear the selected locaiton
          handleSelectedLocation(null);
        }

        // Centers Map to current selected location if there are no branches and not user dragged
        if (selectedLocationItem && !utils.isArrayNotEmpty(locationsParam) && !isUserDraggedOrZoomValue) {
          const { latitude, longitude } = selectedLocationItem.gps;
          mapRef.current.setCenter({ lat: Number(latitude), lng: Number(longitude) });
        }

        updateRecenterButtonVisibility(selectedLocationItem, isUserDraggedOrZoom, hasLocations);
      }

      // If no location selected get default country
      if (!selectedLocationItem) {
        // @dev TODO: If no location is selected, we should center them to their default country defaultCountry
        // This task is being tracked in ticket https://jira.ehi.com/browse/AWR-1139
      }
    },
    [locations, selectedLocationProp, timeStampProp, isUserDraggedOrZoom]
  );

  // @dev: This component should only rerender when Location Tiles expand/collapse
  // Or when timestamp is updated to force it to rerender
  // Or when Recenter button is toggled
  // Or when Location Tile on Mobile/Tablet map is toggled
  // Any map related updates should not cause it to rerender. Gmap handles those states on its own
  useEffect(() => {
    // Store Google Maps Refs
    if (!mapRef.current && mapProp && googleMapsProp) {
      mapRef.current = mapProp;
      googleMapsRef.current = googleMapsProp;
      addMapListeners(); // Add Map Event Listeners
    }
    if (googleMapsRef.current) {
      // Centers Map to new inputed searched location
      updateSelectedLocation(selectedLocationProp, locations);

      // Clears and Redraws new Markers based on new location results
      updateMarkers(locations, selectedLocationProp, timeStampProp, isUserDraggedOrZoom);

      // Expand Location Tile details
      selectedLocationId && updateSelectedInternalLocation(selectedLocationId);

      // Set Location Tile hover state
      !selectedLocationId && updateHoveredLocation(hoveredLocationId);

      // If map is visible in mobile and tablet breakpoints, re-render map
      isMapVisible &&
        googleMapsRef.current.event.addListenerOnce(mapRef.current, 'idle', () =>
          fitBounds({ bottom: googleMapsPaddingBottom, top: googleMapsPaddingTop })
        );

      // Disable Google Maps interaction on tile shown
      const isOptionDisabled = !showLocationDetailTile;
      mapRef.current.setOptions({
        draggable: isOptionDisabled,
      });
    }
  }, [
    showRecenterButton,
    showSoldOutAreaBanner,
    showLocationDetailTile,
    showLocationDetailTile && selectedLocationId,
    timeStampProp,
    hoveredLocationId,
  ]);

  return (
    <div className='location-map' role='application'>
      {children}
      <LocationMapCTA
        showSoldOutAreaBanner={showSoldOutAreaBanner}
        showRecenterButton={showRecenterButton}
        hideSoldOutAreaBanner={hideSoldOutAreaBanner}
        handleRecenterMap={handleRecenterMap}
      />
      {isVisible && showLocationDetailTile && (
        <LocationDetailTile
          onSelectLocation={onSelectLocation}
          isReservationFlow={isReservationFlow}
          handleSelectedLocation={handleDeselectOfBranch}
          handleExpandLocation={handleExpandLocation}
          handleAdjustDateTime={handleAdjustDateTime}
          activeMarker={activeMarker.current}
        />
      )}
    </div>
  );
};

LocationMap.propTypes = propTypes;

LocationMap.defaultProps = {
  mapContainerClassName: 'google-map__container',
  showCurrentLocation: true,
};

export default GoogleMapHOC(LocationMap);
